/**
 * Federate OIDC Authentication Context.
 *
 * Flow: Federate implicit flow → id_token in URL hash → cookie storage
 *       → X-Id-Token header on every Gateway request.
 *
 * Simplified from AgentHubPortal AuthContext — removed QingFeng/MAP/SalesHierarchy
 * permission logic; kept only core auth mechanics.
 */

import { createContext, useContext, useEffect, useState, useMemo, useRef } from 'react';
import { decodeJwt, deleteCookie, getCookie, setCookie, type JwtPayload } from './authStorage';
import { resetAccessTokenLoop, startAccessTokenLoop, storeAccessToken } from './accessTokenLoop';
import { useNavigate } from 'react-router-dom';
import { appConfig, FEDERATE, AUTH_CODE_FLOW_ENABLED } from '../config';
import { readDeepLinkFromUrl, stashDeepLink } from '../utils/emailDeepLink';

// ── Types ───────────────────────────────────────────────────────────────────

export interface UserContext {
  /** Login alias (e.g. "fuxin") */
  uid: string;
  /** Same as uid — Federate JWT "sub" claim */
  sub: string;
  /** Email address */
  email: string;
  /** Display name */
  display_name: string;
  /**
   * Federate "GIVEN_NAME" claim, e.g. "Fan" for "Huang, Fan". Empty when the token
   * omits it. Surfaced because display_name falls back to the login alias when the
   * token carries no display_name claim, which reads as an id rather than a person
   * in anything that greets the viewer.
   */
  given_name: string;
  /** JWT expiration (unix seconds) */
  exp: number;
  /** LDAP group memberships */
  ldap_groups: string[];
  /** Whether the user is authenticated */
  isAuthenticated: boolean;
  /** Trigger logout (clear cookies + redirect) */
  logout: () => void;
}

const EMPTY_USER: UserContext = {
  uid: '',
  sub: '',
  email: '',
  display_name: '',
  given_name: '',
  exp: 0,
  ldap_groups: [],
  isAuthenticated: false,
  logout: () => {},
};

export const AuthContext = createContext<UserContext>(EMPTY_USER);
export const useAuth = () => useContext(AuthContext);

// ── Nonce management ────────────────────────────────────────────────────────

const NONCE_STORAGE_KEY = 'rocky_oidc_nonce';
// CSRF state for the auth-code flow (fail-closed compare on callback).
const STATE_STORAGE_KEY = 'rocky_oidc_state';
// Upper bound on the silent-refresh BFF code exchange (see exchangeSilentCode).
const SILENT_EXCHANGE_TIMEOUT_MS = 10_000;

function storeNonce(nonce: string): void {
  sessionStorage.setItem(NONCE_STORAGE_KEY, nonce);
}

function consumeNonce(): string | null {
  const nonce = sessionStorage.getItem(NONCE_STORAGE_KEY);
  if (nonce) sessionStorage.removeItem(NONCE_STORAGE_KEY);
  return nonce;
}

/** CSPRNG random token (URL-safe) for the OAuth `state` CSRF parameter. */
function randomStateToken(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

// ── Federate URL builder ────────────────────────────────────────────────────

function buildFederateUrl(): string {
  const url = new URL(FEDERATE.authEndpoint);
  url.searchParams.set('client_id', appConfig.federateClientId);
  url.searchParams.set('redirect_uri', window.location.origin);
  url.searchParams.set('scope', 'openid');

  const appState = window.location.pathname + window.location.search;

  if (AUTH_CODE_FLOW_ENABLED) {
    // Authorization-Code flow: ask only for `code`; the Gateway BFF exchanges
    // it server-side. CSRF `state` = CSPRNG token (stored for callback compare)
    // plus the post-login path so we can restore it after the round-trip.
    const stateToken = randomStateToken();
    sessionStorage.setItem(STATE_STORAGE_KEY, stateToken);
    url.searchParams.set('response_type', 'code');
    url.searchParams.set('state', btoa(JSON.stringify({ csrf: stateToken, path: appState })));
    return url.toString();
  }

  // Legacy implicit flow: tokens come back in the URL hash.
  const nonce = crypto.randomUUID();
  storeNonce(nonce);
  url.searchParams.set('response_type', 'id_token token');
  url.searchParams.set('nonce', nonce);
  url.searchParams.set('state', btoa(JSON.stringify({ path: appState })));
  return url.toString();
}

// ── Token validation ────────────────────────────────────────────────────────

/** Validate JWT claims: expiry, issuer, audience, and nonce (on fresh login). */
function validateToken(jwt: JwtPayload, opts?: { checkNonce?: boolean }): boolean {
  // Expiry: allow 60s grace
  if (jwt.exp * 1000 < Date.now() + 60_000) return false;

  // Issuer: strict exact match (required claim)
  const expectedIssuer = `https://${appConfig.identityProvider}`;
  if (!jwt.iss || jwt.iss !== expectedIssuer) {
    console.warn('[auth] Issuer mismatch: expected', expectedIssuer, 'got', jwt.iss);
    return false;
  }

  // Audience: strict exact match (required claim)
  if (!jwt.aud || jwt.aud !== appConfig.federateClientId) {
    console.warn('[auth] Audience mismatch: expected', appConfig.federateClientId, 'got', jwt.aud);
    return false;
  }

  // Nonce: fail-closed — reject if stored nonce is missing or mismatched
  if (opts?.checkNonce) {
    const expectedNonce = consumeNonce();
    if (!expectedNonce || jwt.nonce !== expectedNonce) {
      console.warn('[auth] Nonce validation failed — missing or mismatched');
      return false;
    }
  }

  return true;
}

// ── Token parsing ───────────────────────────────────────────────────────────

/** Parse tokens from URL hash (after Federate redirect) */
function parseTokensFromHash(): string | null {
  const hash = window.location.hash;
  if (!hash || hash.length < 2) return null;

  // Always clean hash first — remove tokens from URL regardless of validation outcome
  const savedHash = hash;
  window.history.replaceState(null, '', window.location.pathname + window.location.search);

  const params = new URLSearchParams(savedHash.slice(1));
  const idToken = params.get('id_token');
  const accessToken = params.get('access_token');

  // Restore pre-login path from OIDC state parameter (survives cross-browser context switches)
  const stateParam = params.get('state');
  if (stateParam) {
    try {
      const { path } = JSON.parse(atob(stateParam));
      if (path && path !== '/') {
        sessionStorage.setItem('rocky_post_login_path', path);
      }
    } catch { /* malformed state — ignore */ }
  }

  if (!idToken) return null;

  try {
    const jwt = decodeJwt(idToken);
    if (!validateToken(jwt, { checkNonce: true })) {
      console.error('[auth] Token validation failed');
      return null;
    }
    setCookie(FEDERATE.idTokenKey, idToken, jwt.exp * 1000);
    if (accessToken) {
      storeAccessToken(accessToken);
    }
  } catch (e) {
    console.error('[auth] Failed to decode id_token from hash', e);
    return null;
  }

  return idToken;
}

/**
 * Auth-code flow callback: if the URL carries ?code&state, POST it to the
 * Gateway BFF (/auth/token), which exchanges it server-side (holds the
 * client_secret) and sets the refresh_token as an HttpOnly cookie. The
 * response body carries id/access tokens, which we store in JS-readable
 * cookies for the SigV4 SkillClient path.
 *
 * Returns the new id_token on success, or null if there was no code / the
 * exchange failed. The one-time code + state are always stripped from the URL
 * first (regardless of outcome) so a stale code can't be replayed.
 */
async function exchangeCodeFromUrl(): Promise<string | null> {
  const params = new URLSearchParams(window.location.search);
  const code = params.get('code');
  const state = params.get('state');
  if (!code) return null;

  // Strip code+state from the URL immediately (before any await).
  const cleanedSearch = new URLSearchParams(window.location.search);
  cleanedSearch.delete('code');
  cleanedSearch.delete('state');
  const qs = cleanedSearch.toString();
  window.history.replaceState(null, '', window.location.pathname + (qs ? `?${qs}` : ''));

  // CSRF: fail-closed compare against the stored CSPRNG state token.
  const expectedState = sessionStorage.getItem(STATE_STORAGE_KEY);
  sessionStorage.removeItem(STATE_STORAGE_KEY);
  let postLoginPath: string | undefined;
  try {
    const parsed = JSON.parse(atob(state || ''));
    if (!expectedState || parsed?.csrf !== expectedState) {
      console.error('[auth] OAuth state mismatch — possible CSRF, aborting exchange');
      return null;
    }
    if (parsed?.path && parsed.path !== '/') postLoginPath = parsed.path;
  } catch {
    console.error('[auth] Malformed OAuth state — aborting exchange');
    return null;
  }

  try {
    const resp = await fetch(FEDERATE.tokenEndpoint, {
      method: 'POST',
      credentials: 'include', // receive the HttpOnly refresh cookie
      headers: { 'Content-Type': 'application/json' },
      // Send the redirect_uri used at authorize time (window.location.origin);
      // the BFF echoes it into the code exchange so it byte-matches what
      // Federate bound to the code. On deployed stages the BFF ignores it
      // unless it is in the server-side allowlist, defaulting to the fixed
      // stage origin — this only unblocks the local dev origin (:3000).
      body: JSON.stringify({ code, state, redirect_uri: window.location.origin }),
    });
    if (!resp.ok) {
      console.error('[auth] Code exchange failed:', resp.status);
      return null;
    }
    const data = await resp.json();
    const idToken: string | undefined = data.id_token;
    if (!idToken) {
      console.error('[auth] Code exchange response missing id_token');
      return null;
    }
    const jwt = decodeJwt(idToken);
    // No nonce in code flow; validate issuer/audience/expiry.
    if (!validateToken(jwt)) {
      console.error('[auth] Exchanged id_token failed validation');
      return null;
    }
    setCookie(FEDERATE.idTokenKey, idToken, jwt.exp * 1000);
    if (data.access_token) storeAccessToken(data.access_token, data.expires_in);
    if (postLoginPath) sessionStorage.setItem('rocky_post_login_path', postLoginPath);
    return idToken;
  } catch (e) {
    console.error('[auth] Code exchange request error', e);
    return null;
  }
}

/** Read token from cookie, validate expiry + claims */
function getTokenFromCookie(): string | null {
  const idToken = getCookie(FEDERATE.idTokenKey);
  if (!idToken) return null;

  try {
    const jwt = decodeJwt(idToken);
    if (!validateToken(jwt)) return null;
    return idToken;
  } catch {
    return null;
  }
}

/** Get valid id_token from hash or cookie; redirect to login if none */
function getToken(): string | null {
  return parseTokensFromHash() || getTokenFromCookie();
}

const REDIRECT_GUARD_KEY = 'rocky_auth_redirect_ts';

/** Redirect to Federate login, saving current path (strip hash to avoid token leak).
 *  Guarded: if we redirected within the last 10s, abort to prevent loops. */
function redirectToLogin(): void {
  const lastRedirect = sessionStorage.getItem(REDIRECT_GUARD_KEY);
  if (lastRedirect && Date.now() - Number(lastRedirect) < 10_000) {
    console.error('[auth] Redirect loop detected — aborting');
    return;
  }
  sessionStorage.setItem(REDIRECT_GUARD_KEY, String(Date.now()));
  const existingPath = sessionStorage.getItem('rocky_post_login_path');
  if (!existingPath || existingPath === '/') {
    sessionStorage.setItem(
      'rocky_post_login_path',
      window.location.pathname + window.location.search,
    );
  }
  window.location.assign(buildFederateUrl());
}

// ── Silent refresh via hidden iframe ────────────────────────────────────────

/**
 * Exchange a silent-flow authorization code for fresh tokens via the BFF.
 *
 * POSTs {code, state, redirect_uri} to /auth/token; the Gateway performs the
 * confidential-client code exchange server-side and — when at-rest persistence
 * is enabled — writes the tokens to AgentUserToken. That persistence is what
 * keeps the Slack /internal/user-token read from missing (the legacy implicit
 * silent flow never hit /auth/token, so the DDB row went stale and the Slack
 * sign-in looped). Sets the JS-readable id/access cookies. Returns the new
 * id_token, or undefined on any failure (caller falls back to a full-page
 * redirect). Never logs the code or token values.
 */
async function exchangeSilentCode(code: string, state: string): Promise<string | undefined> {
  try {
    const resp = await fetch(FEDERATE.tokenEndpoint, {
      method: 'POST',
      credentials: 'include', // receive/rotate the HttpOnly refresh cookie
      headers: { 'Content-Type': 'application/json' },
      // fetch() has no built-in timeout; a stalled connection would otherwise
      // leave refreshAuth()'s shared promise pending forever with no redirect.
      signal: AbortSignal.timeout(SILENT_EXCHANGE_TIMEOUT_MS),
      body: JSON.stringify({
        code,
        state,
        redirect_uri: `${window.location.origin}/silent-callback.html`,
      }),
    });
    if (!resp.ok) {
      console.warn('[auth] Silent code exchange failed:', resp.status);
      return undefined;
    }
    const data = await resp.json();
    const idToken: string | undefined = data.id_token;
    if (!idToken) return undefined;
    const jwt = decodeJwt(idToken);
    // Code flow carries no nonce; validate issuer/audience/expiry.
    if (!validateToken(jwt)) return undefined;
    setCookie(FEDERATE.idTokenKey, idToken, jwt.exp * 1000);
    if (data.access_token) storeAccessToken(data.access_token, data.expires_in);
    return idToken;
  } catch {
    console.warn('[auth] Silent code exchange error');
    return undefined;
  }
}

/**
 * Attempt to obtain a fresh id_token without user interaction.
 *
 * How it works: create a hidden iframe pointing at the Federate authorize
 * endpoint (Authorization-Code flow, prompt=none). If the user's Federate SSO
 * session is still alive the IdP redirects straight back to
 * silent-callback.html?code=…; we read the code from the same-origin iframe
 * URL and exchange it through the Gateway BFF (/auth/token). The browser never
 * talks to Federate's token endpoint itself — every token mint goes through the
 * Gateway, which persists the result to AgentUserToken as a side effect.
 *
 * Returns the new id_token on success, or undefined on failure.
 */
async function silentRefresh(): Promise<string | undefined> {
  try {
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';

    // Authorization-Code flow in a hidden iframe. With prompt=none, a live
    // Federate SSO session redirects straight back to silent-callback.html with
    // ?code (no login UI). We read the code from the same-origin iframe URL and
    // exchange it via the BFF /auth/token, which PERSISTS the tokens to
    // AgentUserToken — unlike the legacy implicit flow, which never hit
    // /auth/token and so left the DDB row stale (Slack sign-in loop).
    //
    // Deliberately NOT gated on AUTH_CODE_FLOW_ENABLED: the BFF exchange works
    // regardless of how the initial login was obtained, and the implicit
    // (id_token-in-fragment) silent refresh is what we are retiring. Turning
    // that flag off rolls back the top-level login only; silent refresh stays
    // on the code flow.
    //
    // CSRF state lives in this closure, not in sessionStorage: the parent
    // window is alive for the whole round-trip, and a per-call local avoids
    // the collision two overlapping silentRefresh() calls (proactive timer +
    // reactive 401) would have on a shared storage key.
    const state = randomStateToken();
    const url = new URL(FEDERATE.authEndpoint);
    url.searchParams.set('client_id', appConfig.federateClientId);
    url.searchParams.set('redirect_uri', `${window.location.origin}/silent-callback.html`);
    url.searchParams.set('response_type', 'code');
    url.searchParams.set('scope', 'openid');
    url.searchParams.set('state', state);
    // prompt=none: an expired SSO session yields an error/cross-origin login
    // page instead of a prompt, so we fall through to a full-page redirect.
    url.searchParams.set('prompt', 'none');
    iframe.src = url.toString();

    document.body.appendChild(iframe);

    return await new Promise<string | undefined>((resolve) => {
      // Single settle point: the 10s timeout and the onload/onerror paths race,
      // so whichever runs first wins and the rest become no-ops. Without this,
      // a slow /auth/token exchange could time out into resolve(undefined) — a
      // full-page redirect — even though the exchange then succeeds and writes
      // fresh cookies.
      let settled = false;
      function settle(value: string | undefined) {
        if (settled) return;
        settled = true;
        clearTimeout(timeout);
        try { document.body.removeChild(iframe); } catch { /* already removed */ }
        resolve(value);
      }

      const timeout = setTimeout(() => {
        console.warn('[auth] Silent refresh timed out');
        settle(undefined);
      }, 10_000);

      iframe.onload = async () => {
        try {
          const iframeHref = iframe.contentWindow?.location.href;
          if (!iframeHref) {
            settle(undefined);
            return;
          }
          // Same-origin read of silent-callback.html?code=...&state=...
          const params = new URL(iframeHref).searchParams;
          const code = params.get('code');
          const returnedState = params.get('state');
          // CSRF: compare against this call's own state (closure-local).
          if (!code || returnedState !== state) {
            // No code (prompt=none returned login_required) or CSRF mismatch.
            settle(undefined);
            return;
          }
          // The iframe round-trip (the part this timer guards) is done; stop
          // the clock so BFF latency can't turn a successful mint into a
          // spurious redirect. The exchange has its own bound: fetch() in
          // exchangeSilentCode aborts via AbortSignal.timeout, and a rejected
          // fetch resolves to undefined there, so the promise always settles.
          clearTimeout(timeout);
          const newIdToken = await exchangeSilentCode(code, returnedState);
          settle(newIdToken);
        } catch (e) {
          // Cross-origin access error means the IdP showed a login page
          // (different origin) — silent refresh not possible.
          console.warn('[auth] Silent refresh cross-origin — Federate session expired');
          settle(undefined);
        }
      };

      iframe.onerror = () => {
        settle(undefined);
      };
    });
  } catch (error) {
    console.error('[auth] Silent refresh failed:', error);
    return undefined;
  }
}

// ── Public: get id_token for API calls ──────────────────────────────────────

/**
 * Returns the current Federate id_token if valid, or undefined.
 * Used by GatewayClient to attach X-Id-Token header.
 */
export function getIdToken(): string | undefined {
  const devToken = import.meta.env.DEV ? import.meta.env.VITE_DEV_ID_TOKEN : undefined;
  return getTokenFromCookie() ?? devToken ?? undefined;
}

/**
 * Attempt to refresh the auth token silently, then return the new id_token.
 * Falls back to a full-page redirect if silent refresh fails.
 *
 * Called by GatewayClient on 401 — allows the client to retry the request
 * with a fresh token before giving up.
 *
 * @returns The new id_token string, or undefined if a redirect was triggered.
 */
let _refreshing: Promise<string | undefined> | null = null;

/**
 * Silent refresh = the hidden-iframe Authorization-Code flow above.
 *
 * The id_token is only ever renewed through /auth/token: the Federate client
 * profile does not re-issue an id_token on the refresh_token grant. That grant
 * renews the ACCESS token only and runs on its own schedule in accessTokenLoop.ts.
 */
async function doSilentRefresh(): Promise<string | undefined> {
  return silentRefresh();
}

export async function refreshAuth(): Promise<string | undefined> {
  // De-duplicate concurrent refresh calls (e.g. multiple parallel 401s)
  if (_refreshing) return _refreshing;

  _refreshing = (async () => {
    try {
      const newToken = await doSilentRefresh();
      if (newToken) {
        console.log('[auth] Refresh succeeded');
        return newToken;
      }
    } catch (e) {
      console.warn('[auth] Refresh error:', e);
    }

    // Refresh failed → full redirect (guarded against loops)
    console.warn('[auth] Refresh failed, redirecting to login');
    redirectToLogin();
    return undefined;
  })();

  try {
    return await _refreshing;
  } finally {
    _refreshing = null;
  }
}

// ── Provider ────────────────────────────────────────────────────────────────

export function AuthContextProvider({ children }: { children: React.ReactNode }) {
  const [idToken, setIdToken] = useState<string | null>(null);
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  useEffect(() => {
    let cancelled = false;

    function onResolved(token: string | null) {
      if (cancelled) return;
      if (token) {
        setIdToken(token);
        // Restore pre-login path via React Router navigate
        const savedPath = sessionStorage.getItem('rocky_post_login_path');
        if (savedPath && savedPath !== '/' && savedPath !== window.location.pathname) {
          sessionStorage.removeItem('rocky_post_login_path');
          navigateRef.current(savedPath, { replace: true });
        } else {
          sessionStorage.removeItem('rocky_post_login_path');
        }
      } else {
        // Stash any email deep-link before we leave the origin — sessionStorage
        // survives the Federate round-trip; the prompt may exceed OIDC `state` limits.
        const dl = readDeepLinkFromUrl();
        if (dl) stashDeepLink(dl);
        // No valid token → redirect to Federate login
        redirectToLogin();
      }
    }

    if (AUTH_CODE_FLOW_ENABLED) {
      // Auth-code flow: a valid cookie session short-circuits; otherwise try to
      // exchange a ?code from the callback before deciding to redirect.
      const existing = getTokenFromCookie();
      if (existing) {
        onResolved(existing);
      } else {
        exchangeCodeFromUrl().then((token) => onResolved(token)).catch(() => onResolved(null));
      }
    } else {
      // Legacy implicit flow: tokens are in the URL hash or an existing cookie.
      onResolved(getToken());
    }

    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  // ── Proactive silent refresh: schedule refresh 20 min before expiry ───────
  useEffect(() => {
    if (!idToken) return;

    let timerId: ReturnType<typeof setTimeout> | null = null;

    function scheduleRefresh(token: string) {
      try {
        const jwt = decodeJwt(token);
        const msUntilExpiry = jwt.exp * 1000 - Date.now();
        // Refresh 20 minutes before expiry. Floor at 30s (not 0) so a token
        // already inside the skew window can't create a tight refresh loop;
        // the reactive 401 retry is the backstop for immediate expiry.
        const refreshIn = Math.max(msUntilExpiry - 20 * 60 * 1000, 30_000);

        console.log(`[auth] Proactive refresh scheduled in ${Math.round(refreshIn / 1000)}s`);

        timerId = setTimeout(async () => {
          const newToken = await doSilentRefresh();
          if (newToken) {
            console.log('[auth] Proactive refresh succeeded');
            setIdToken(newToken);
            // Next timer will be set by this useEffect re-running due to idToken change
          } else {
            console.warn('[auth] Proactive refresh failed — will fall back to 401 refresh');
          }
        }, refreshIn);
      } catch {
        // Token decode failed — skip scheduling
      }
    }

    scheduleRefresh(idToken);

    return () => {
      if (timerId) clearTimeout(timerId);
    };
  }, [idToken]);

  // Access-token loop (POST /auth/refresh) — see accessTokenLoop.ts.
  useEffect(() => (idToken ? startAccessTokenLoop() : undefined), [idToken]);

  const contextValue = useMemo<UserContext>(() => {
    if (!idToken) return EMPTY_USER;

    try {
      const jwt = decodeJwt(idToken);
      return {
        uid: jwt.sub,
        sub: jwt.sub,
        email: jwt.email || jwt.EMAIL || (jwt.sub ? jwt.sub + '@amazon.com' : ''),
        display_name: jwt.display_name || jwt.sub,
        given_name: jwt.GIVEN_NAME || '',
        exp: jwt.exp,
        ldap_groups: jwt.ldap_groups || [],
        isAuthenticated: true,
        logout: () => {
          deleteCookie(FEDERATE.idTokenKey);
          deleteCookie(FEDERATE.accessTokenKey);
          resetAccessTokenLoop();
          // Auth-code flow: also clear the server-side HttpOnly refresh cookie.
          // Fire-and-forget — navigate home regardless of the request outcome.
          if (AUTH_CODE_FLOW_ENABLED) {
            fetch(FEDERATE.logoutEndpoint, { method: 'POST', credentials: 'include' }).catch(
              () => {},
            );
          }
          window.location.href = '/';
        },
      };
    } catch {
      return EMPTY_USER;
    }
  }, [idToken]);

  // Don't render children until we have a valid token (or are redirecting)
  if (!idToken) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <p>Redirecting to login...</p>
      </div>
    );
  }

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
}
